import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useState, type ReactNode } from "react";

export type InlineSelectOption<T extends string> = {
  value: T;
  label: string;
  icon?: ReactNode;
};

export function InlineSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly InlineSelectOption<T>[];
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  return <div onBlurCapture={(event) => {
    const next = event.relatedTarget as Node | null;
    if (!next || !event.currentTarget.contains(next)) setOpen(false);
  }}>
    <span className="m-field-label">{label}</span>
    <button
      type="button"
      className="m-text-input m-flex m-items-center m-justify-between m-gap-2 m-text-left"
      role="combobox"
      aria-label={label}
      aria-expanded={open}
      onClick={() => setOpen((current) => !current)}
    >
      <span className="m-flex m-min-w-0 m-items-center m-gap-1.5">{selected?.icon ? <span aria-hidden="true" className="m-shrink-0">{selected.icon}</span> : null}<span className="m-truncate">{selected?.label}</span></span>
      {open ? <ChevronUp className="m-size-5 m-shrink-0" /> : <ChevronDown className="m-size-5 m-shrink-0" />}
    </button>
    {open ? <div role="listbox" aria-label={label} className="m-mt-1 m-max-h-60 m-overflow-y-auto m-rounded-xl m-border m-border-border m-bg-surface">
      {options.map((option) => <button
        type="button"
        role="option"
        aria-selected={option.value === value}
        key={option.value}
        className="m-flex m-min-h-12 m-w-full m-items-center m-gap-2 m-border-b m-border-border m-px-3 m-text-left last:m-border-b-0"
        onClick={() => { onChange(option.value); setOpen(false); }}
      >
        <Check className={`m-size-5 m-shrink-0 m-text-primary ${option.value === value ? "m-opacity-100" : "m-opacity-0"}`} />
        {option.icon ? <span aria-hidden="true" className="m-shrink-0">{option.icon}</span> : null}
        <span>{option.label}</span>
      </button>)}
    </div> : null}
  </div>;
}
